<template>
  <div id="app" :class="appClasses">
    <!-- Navigation Header - Show when user is authenticated -->
    <nav v-if="shouldShowNavbar" class="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div class="container-fluid">
        <a class="navbar-brand gradient-text fw-bold" href="#" @click="goToHome">
            <i class="bi bi-mortarboard me-2"></i>
            LifeSkill
          </a>
          
          <div class="d-flex align-items-center">
            <!-- Notifications Bell -->
            <div class="dropdown me-3" v-if="currentUser">
              <button class="btn btn-outline-secondary position-relative" type="button" data-bs-toggle="dropdown" @click="loadRecentNotifications">
                <i class="bi bi-bell"></i>
                <span v-if="unreadCount>0" class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">{{ unreadCount }}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end" style="min-width: 320px; max-height: 360px; overflow: auto;">
                <li class="dropdown-header fw-bold">Notifications</li>
                <li v-if="notifications.length===0" class="px-3 py-2 text-muted small">No notifications</li>
                <li v-for="n in notifications" :key="n.id || n.timestamp" class="px-3 py-2 border-bottom">
                  <div class="small fw-bold">{{ n.title || 'Notification' }}</div>
                  <div class="small text-muted">{{ n.content }}</div>
                  <div class="small text-nowrap text-secondary">{{ formatWhen(n.timestamp) }}</div>
                </li>
                <li><hr class="dropdown-divider"/></li>
                <li><button class="dropdown-item" @click="markAllRead">Mark all read</button></li>
              </ul>
            </div>
            <!-- User Info -->
            <div class="dropdown">
              <button 
                class="btn btn-outline-primary dropdown-toggle d-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
              >
                <div class="avatar me-2">{{ currentUser?.avatar || 'U' }}</div>
                {{ currentUser?.name || 'User' }}
              </button>
              <ul class="dropdown-menu">
                <li><a class="dropdown-item" href="#" @click.prevent="goToProfile"><i class="bi bi-person me-2"></i>Profile</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="goToSettings"><i class="bi bi-gear me-2"></i>Settings</a></li>
                <li><hr class="dropdown-divider"></li>
                <li><button class="dropdown-item" @click.prevent="handleLogout"><i class="bi bi-box-arrow-right me-2"></i>Logout</button></li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

    <!-- Main Content Area -->
    <div :class="{ 'main-content': shouldShowNavbar }">
      <!-- Router View - This will display the current route component -->
      <router-view />
    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      currentUser: null,
      notifications: [],
      unreadCount: 0
    }
  },
  computed: {
    appClasses() {
      return [
        this.currentUser ? `${this.currentUser.role}-theme` : 'student-theme'
      ]
    },
    shouldShowNavbar() {
      // Show navbar only if user is authenticated AND not on login/register pages
      return this.currentUser && !['/', '/register'].includes(this.$route.path)
    }
  },
  watch: {
    // Watch for route changes and re-check authentication
    '$route'() {
      this.checkExistingAuth()
    }
  },
  mounted() {
    
    // Check for existing authentication
    this.checkExistingAuth()
    // Start realtime after auth
    this.tryStartRealtime()
    
    // Add a global method to debug authentication
    window.debugAuth = () => {
      // intentionally no-op in production
    }
    
    // Theme initialization removed - using light theme only
  },
  methods: {
    checkExistingAuth() {
      // Check if user is already logged in
      const storedUser = localStorage.getItem('currentUser')
      
      if (storedUser) {
        try {
          this.currentUser = JSON.parse(storedUser)
          
          // Apply theme
          document.body.className = this.appClasses.join(' ')
          
          // If user is on login/register page but is authenticated, redirect to dashboard
          if (['/', '/register'].includes(this.$route.path)) {
            this.goToHome()
          }
          // Ensure realtime connection starts when user is present
          this.tryStartRealtime()
        } catch (error) {
          console.error('App.vue - Error parsing stored user:', error)
          localStorage.removeItem('currentUser')
          this.currentUser = null
        }
      } else {
        // No stored user, make sure currentUser is null
        this.currentUser = null
      }
    },

    async tryStartRealtime() {
      if (!this.currentUser) return
      try {
        const { default: realtime } = await import('./services/realtimeService.js')
        realtime.connect()
        // Update in-memory counter on realtime events
        realtime.subscribe((evt) => {
          if (!evt) return
          if (evt.type === 'notification' || evt.type === 'message' || evt.type === 'announcement') {
            this.unreadCount = Math.max(0, (this.unreadCount || 0) + 1)
          }
        })
      } catch (e) {
        console.warn('Realtime init failed', e)
      }
    },

    async loadRecentNotifications() {
      try {
        const { notificationService } = await import('./services/notificationService.js')
        const res = await notificationService.list({ includeRead: false, limit: 20 })
        this.notifications = res.items || []
        this.unreadCount = (this.notifications || []).filter(n => !n.read).length
      } catch (e) {
        // ignore
      }
    },

    async markAllRead() {
      try {
        const ids = (this.notifications || []).map(n => n.id).filter(Boolean)
        if (ids.length === 0) return
        const { notificationService } = await import('./services/notificationService.js')
        await notificationService.markRead(ids)
        this.unreadCount = 0
        this.notifications = (this.notifications || []).map(n => ({ ...n, read: true }))
      } catch (e) {
        // ignore
      }
    },

    goToHome() {
      // Navigate to appropriate dashboard based on user role
      if (this.currentUser) {
        switch (this.currentUser.role) {
          case 'student':
            this.$router.push('/student-dashboard')
            break
          case 'parent':
            this.$router.push('/parent-dashboard')
            break
          case 'teacher':
            this.$router.push('/teacher-dashboard')
            break
          default:
            this.$router.push('/student-dashboard')
        }
      }
    },

    goToProfile() {
      this.$router.push('/profile')
      // Close the dropdown manually
      this.closeDropdown()
    },

    goToSettings() {
      this.$router.push('/settings')
      // Close the dropdown manually
      this.closeDropdown()
    },

    closeDropdown() {
      // Manually close Bootstrap dropdown
      const dropdownElement = document.querySelector('[data-bs-toggle="dropdown"]')
      if (dropdownElement && window.bootstrap) {
        const dropdown = window.bootstrap.Dropdown.getInstance(dropdownElement)
        if (dropdown) {
          dropdown.hide()
        }
      }
    },
    
    handleLogout() {
      this.currentUser = null
      localStorage.removeItem('currentUser')
      document.body.className = ''
      // Close the dropdown manually
      this.closeDropdown()
      // Redirect to login page
      this.$router.push('/')
    },

    formatWhen(iso) {
      if (!iso) return ''
      try { return new Date(iso).toLocaleString() } catch { return '' }
    }
  }
}
</script>

<style scoped>

/* App-specific styles */

.app-container {
  min-height: 100vh;
  background: var(--bg-secondary);
}

.main-content {
  padding-top: 76px; /* Account for fixed navbar */
}

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1030;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--role-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  border: 2px solid white;
}

.demo-credentials {
  background: var(--bg-secondary);
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
}

@media (max-width: 768px) {
  .login-card {
    padding: 2rem;
    margin: 1rem;
  }
}
</style>
